import {
  FormControl,
  AbstractControl,
  Validators,
  ValidationErrors,
  ValidatorFn
} from "@angular/forms";

import { FormData } from "./classes/data";
import { FormSchema } from "./classes/schema";
import { FormSchemaData } from "./form.interface";

import * as _ from "lodash";
import { Modernizr } from "../../vendor/modernizr";
import * as _moment from "moment";
/**
 * @ignore
 */
const moment = _moment;

export interface FormSchemaValidations {
  required?: boolean | FormValidationBooleanFunction;
  requiredTrue?: boolean | FormValidationBooleanFunction;
  minLength?: number | FormValidationNumberFunction;
  maxLength?: number | FormValidationNumberFunction;
  pattern?: string | RegExp | FormValidationRegExpFunction;
  email?: boolean | FormValidationBooleanFunction;
  phone?: boolean | FormValidationBooleanFunction;
  cpf?: boolean | FormValidationBooleanFunction;
  cnpj?: boolean | FormValidationBooleanFunction;
  rg?: boolean | FormValidationBooleanFunction;
  cep?: boolean | FormValidationBooleanFunction;
  date?: boolean | FormValidationBooleanFunction;
  minDate?: FormValidationDateRule | FormValidationDateFunction;
  maxDate?: FormValidationDateRule | FormValidationDateFunction;
  time?: boolean | FormValidationBooleanFunction;
  minTime?: string | FormValidationStringFunction;
  maxTime?: string | FormValidationStringFunction;
  min?: number | FormValidationNumberFunction;
  max?: number | FormValidationNumberFunction;
  equals?: string | FormValidationStringFunction;
  fileType?: Array<string> | string | FormValidationArrayStringFunction;
}

export interface FormSchemaValidationMessages {
  custom?: FormSchemaValidationMessage;
  required?: FormSchemaValidationMessage;
  requiredTrue?: FormSchemaValidationMessage;
  minLength?: FormSchemaValidationMessage;
  maxLength?: FormSchemaValidationMessage;
  pattern?: FormSchemaValidationMessage;
  email?: FormSchemaValidationMessage;
  phone?: FormSchemaValidationMessage;
  cpf?: FormSchemaValidationMessage;
  cnpj?: FormSchemaValidationMessage;
  rg?: FormSchemaValidationMessage;
  cep?: FormSchemaValidationMessage;
  date?: FormSchemaValidationMessage;
  minDate?: FormSchemaValidationMessage;
  maxDate?: FormSchemaValidationMessage;
  time?: FormSchemaValidationMessage;
  minTime?: FormSchemaValidationMessage;
  maxTime?: FormSchemaValidationMessage;
  min?: FormSchemaValidationMessage;
  max?: FormSchemaValidationMessage;
  equals?: FormSchemaValidationMessage;
  fileType?: FormSchemaValidationMessage;
}

/**
 * @ignore
 */
export class FormValidators {
  static custom(rule: void, key: string, form: FormData): ValidatorFn {
    const validator_fn = (
      control: AbstractControl
    ): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      return schema && !!schema.custom_error_message ? { custom: true } : null;
    };
    return validator_fn;
  }

  static required(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (
      control: AbstractControl
    ): ValidationErrors | null => {
      const schema = form.getSchema(key);

      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;

      return required && isEmptyInputValue(control.value)
        ? { required: true }
        : null;
    };
    return validator_fn;
  }

  static requiredTrue(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (
      control: AbstractControl
    ): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      return required && control.value === true ? null : { required: true };
    };
    return validator_fn;
  }

  static minLength(
    rule: number | FormValidationNumberFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (
      control: AbstractControl
    ): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const minLength: number = _.isFunction(rule)
        ? (<FormValidationNumberFunction>rule)(schema, form)
        : <number>rule;
      if (!_.isNumber(minLength)) {
        return null;
      }

      if (isEmptyInputValue(control.value)) {
        return null;
      } // don't validate empty values to allow optional controls

      const length: number = control.value ? control.value.length : 0;
      return length < minLength
        ? { minLength: { requiredLength: minLength, actualLength: length } }
        : null;
    };
    return validator_fn;
  }

  static maxLength(
    rule: number | FormValidationNumberFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (
      control: AbstractControl
    ): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const maxLength: number = _.isFunction(rule)
        ? (<FormValidationNumberFunction>rule)(schema, form)
        : <number>rule;
      if (!_.isNumber(maxLength)) {
        return null;
      }

      const length: number = control.value ? control.value.length : 0;
      return length > maxLength
        ? { maxLength: { requiredLength: maxLength, actualLength: length } }
        : null;
    };
    return validator_fn;
  }

  static pattern(
    rule: string | RegExp | FormValidationRegExpFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (
      control: AbstractControl
    ): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const pattern: string | RegExp = _.isFunction(rule)
        ? (<FormValidationRegExpFunction>rule)(schema, form)
        : <string>rule;
      if (!pattern) {
        return null;
      }

      let regex: RegExp;
      let regexStr: string;
      if (typeof pattern === "string") {
        regexStr = `^${pattern}$`;
        regex = new RegExp(regexStr);
      } else {
        regexStr = pattern.toString();
        regex = pattern;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      return regex.test(value)
        ? null
        : { pattern: { requiredPattern: regexStr, actualValue: value } };
    };
    return validator_fn;
  }

  static email(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      const pattern = /^[a-z0-9!#$%&'*+\/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;
      if (_.isString(value) && (value.length <= 5 || !pattern.test(value))) {
        return { email: true };
      }
      return null;
    };
    return validator_fn;
  }

  static phone(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      const pattern = /(\([0-9]{2}\)\ [0-9]{4,5}\-[0-9]{4})|([0-9]{10,11})/;
      if (_.isString(value) && !pattern.test(value)) {
        return { phone: true };
      }
      return null;
    };
    return validator_fn;
  }

  static cpf(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      const pattern = /([0-9]{3}\.[0-9]{3}.[0-9]{3}\-[0-9]{2})|([0-9]{11})/;
      if (_.isString(value) && !pattern.test(value)) {
        return { cpf: true };
      }
      return null;
    };
    return validator_fn;
  }

  static cnpj(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      const pattern = /([0-9]{2}\.[0-9]{3}.[0-9]{3}\/[0-9]{4}\-[0-9]{2})|([0-9]{14})/;
      if (_.isString(value) && !pattern.test(value)) {
        return { cnpj: true };
      }
      return null;
    };
    return validator_fn;
  }

  static rg(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      const pattern = /([0-9]{2}\.[0-9]{3}.[0-9]{3}\-[0-9]{1})|([0-9]{9})/;
      if (_.isString(value) && !pattern.test(value)) {
        return { rg: true };
      }
      return null;
    };
    return validator_fn;
  }

  static cep(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      const pattern = /([0-9]{2}\.[0-9]{3}\-[0-9]{3})|([0-9]{8})/;
      if (_.isString(value) && !pattern.test(value)) {
        return { cep: true };
      }
      return null;
    };
    return validator_fn;
  }

  static date(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      // let pattern = /^(((0[1-9]|[12]\d|3[01])\/(0[13578]|1[02])\/((19|[2-9]\d)\d{2}))|((0[1-9]|[12]\d|30)\/(0[13456789]|1[012])\/((19|[2-9]\d)\d{2}))|((0[1-9]|1\d|2[0-8])\/02\/((19|[2-9]\d)\d{2}))|(29\/02\/((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/;
      const pattern = /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/([0-9]{4})$/;
      if (_.isString(value) && !pattern.test(value)) {
        return { date: true };
      }
      return null;
    };
    return validator_fn;
  }

  static minDate(
    rule: FormValidationDateRule | FormValidationDateFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const minDate: FormValidationDateRule = _.isFunction(rule)
        ? (<FormValidationDateFunction>rule)(schema, form)
        : <FormValidationDateRule>rule;
      if (
        !moment.isMoment(minDate) &&
        !_.isDate(minDate) &&
        !_.isString(minDate)
      ) {
        return null;
      }

      const pattern = /^[0-9]{4}-[0-9]{2}\-[0-9]{2}$/;
      if (_.isString(minDate) && !pattern.test(<string>minDate)) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls

      const moment_value = _.isString(minDate)
        ? moment(value, "YYYY-MM-DD")
        : moment(value);
      if (moment_value.isBefore(minDate, "day")) {
        return {
          minDate: {
            requiredMinDate: minDate,
            actualDate: moment(value).toDate()
          }
        };
      }
      return null;
    };
    return validator_fn;
  }

  static maxDate(
    rule: FormValidationDateRule | FormValidationDateFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const maxDate: FormValidationDateRule = _.isFunction(rule)
        ? (<FormValidationDateFunction>rule)(schema, form)
        : <FormValidationDateRule>rule;
      if (
        !moment.isMoment(maxDate) &&
        !_.isDate(maxDate) &&
        !_.isString(maxDate)
      ) {
        return null;
      }

      const pattern = /^[0-9]{4}-[0-9]{2}\-[0-9]{2}$/;
      if (_.isString(maxDate) && !pattern.test(<string>maxDate)) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls

      const moment_value = _.isString(maxDate)
        ? moment(value, "YYYY-MM-DD")
        : moment(value);
      if (moment_value.isAfter(maxDate, "day")) {
        return {
          maxDate: {
            requiredMaxDate: maxDate,
            actualDate: moment(value).toDate()
          }
        };
      }
      return null;
    };
    return validator_fn;
  }

  static time(
    rule: boolean | FormValidationBooleanFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const required: boolean = _.isFunction(rule)
        ? (<FormValidationBooleanFunction>rule)(schema, form)
        : <boolean>rule;
      if (!required) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      // let pattern = /^(((0[1-9]|[12]\d|3[01])\/(0[13578]|1[02])\/((19|[2-9]\d)\d{2}))|((0[1-9]|[12]\d|30)\/(0[13456789]|1[012])\/((19|[2-9]\d)\d{2}))|((0[1-9]|1\d|2[0-8])\/02\/((19|[2-9]\d)\d{2}))|(29\/02\/((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/;
      const pattern = /^([0-1][0-9]|2[0-3])\:[0-5][0-9]$/;
      if (_.isString(value) && !pattern.test(value)) {
        return { time: true };
      }
      return null;
    };
    return validator_fn;
  }

  static minTime(
    rule: string | FormValidationStringFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const minTime: string = _.isFunction(rule)
        ? (<FormValidationStringFunction>rule)(schema, form)
        : <string>rule;
      const pattern = /^([0-1][0-9]|2[0-3])\:[0-5][0-9]$/;
      if (!(_.isString(minTime) && pattern.test(minTime))) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls

      const error = {
        minTime: { requiredMinTime: minTime, actualTime: value }
      };
      if (!pattern.test(value)) {
        return error;
      }

      const minTimeInt: number = parseInt(minTime.replace(":", ""));
      const valueInt: number = parseInt(value.replace(":", ""));

      if (valueInt < minTimeInt) {
        return error;
      }
      return null;
    };
    return validator_fn;
  }

  static maxTime(
    rule: string | FormValidationStringFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const maxTime: string = _.isFunction(rule)
        ? (<FormValidationStringFunction>rule)(schema, form)
        : <string>rule;
      const pattern = /^([0-1][0-9]|2[0-3])\:[0-5][0-9]$/;
      if (!(_.isString(maxTime) && pattern.test(maxTime))) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls

      const error = {
        maxTime: { requiredMaxTime: maxTime, actualTime: value }
      };
      if (!pattern.test(value)) {
        return error;
      }

      const maxTimeInt: number = parseInt(maxTime.replace(":", ""));
      const valueInt: number = parseInt(value.replace(":", ""));

      if (valueInt > maxTimeInt) {
        return error;
      }
      return null;
    };
    return validator_fn;
  }

  static min(
    rule: number | FormValidationNumberFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const min: number = _.isFunction(rule)
        ? (<FormValidationNumberFunction>rule)(schema, form)
        : <number>rule;
      if (!_.isNumber(min)) {
        return null;
      }

      const value: string | number = control.value;
      if (isEmptyInputValue(value) || !_.isNumber(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      if (value < min) {
        return { min: { requiredMinValue: min, actualValue: value } };
      }
      return null;
    };
    return validator_fn;
  }

  static max(
    rule: number | FormValidationNumberFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const max: number = _.isFunction(rule)
        ? (<FormValidationNumberFunction>rule)(schema, form)
        : <number>rule;
      if (!_.isNumber(max)) {
        return null;
      }

      const value: string | number = control.value;
      if (isEmptyInputValue(value) || !_.isNumber(value)) {
        return null;
      } // don't validate empty values to allow optional controls
      if (value > max) {
        return { max: { requiredMaxValue: max, actualValue: value } };
      }
      return null;
    };
    return validator_fn;
  }

  static equals(
    rule: string | FormValidationStringFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const equalsKey: string = _.isFunction(rule)
        ? (<FormValidationStringFunction>rule)(schema, form)
        : <string>rule;
      if (!_.isString(equalsKey)) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls

      const equalsSchema = form.getSchema(equalsKey);
      if (!equalsSchema) {
        return null;
      }

      const requiredValue = equalsSchema.value;

      const error = {
        equals: {
          notEqualsSchema: equalsSchema,
          requiredValue: requiredValue,
          actualValue: value
        }
      };
      if (requiredValue !== value) {
        return error;
      }

      return null;
    };
    return validator_fn;
  }

  static fileType(
    rule: Array<string> | string | FormValidationArrayStringFunction,
    key: string,
    form: FormData
  ): ValidatorFn {
    const validator_fn = (control: FormControl): ValidationErrors | null => {
      const schema = form.getSchema(key);
      if (!schema) {
        return null;
      }

      const requiredFileTypes: Array<string> | string = _.isFunction(rule)
        ? (<FormValidationArrayStringFunction>rule)(schema, form)
        : <Array<string> | string>rule;
      if (!_.isArray(requiredFileTypes) && !_.isString(requiredFileTypes)) {
        return null;
      }

      const value: string = control.value;
      if (isEmptyInputValue(value)) {
        return null;
      } // don't validate empty values to allow optional controls

      let valid_type = false;
      const current_type_array: RegExpMatchArray = value.match(
        /data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/
      );
      const current_type: string =
        current_type_array && current_type_array.length
          ? current_type_array[1]
          : null;
      const required_types_array: Array<string> = _.isString(requiredFileTypes)
        ? requiredFileTypes.toString().split(",")
        : _.isArray(requiredFileTypes)
        ? <any>requiredFileTypes
        : null;
      if (_.isEmpty(current_type) || _.isEmpty(required_types_array)) {
        return null;
      }

      const formated_file_types: Array<
        string | Array<string>
      > = required_types_array.map(file_type => {
        switch (file_type) {
          case "image":
            return "image/*";
          case "jpeg":
          case "jpg":
            return "image/jpeg";
          case "png":
            return "image/png";
          case "svg":
            return "image/svg+xml";
          case "gif":
            return "image/gif";

          case "video":
            return "video/*";
          case "mpeg":
          case "mpg":
          case "mpe":
            return "video/mpeg";
          case "mp4":
            return ["video/mp4", "video/mpeg4", "video/x-mpeg-4"];
          case "avi":
            return [
              "video/avi",
              "application/x-troff-msvideo",
              "video/avi",
              "video/msvideo",
              "video/x-msvideo"
            ];

          case "audio":
            return "audio/*";
          case "mp3":
            return ["audio/mp3", "audio/mpeg3", "audio/x-mpeg-3"];

          case "text":
            return "text/plain";
          case "csv":
            return "text/csv";

          case "doc":
          case "docx":
            return [
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ];
          case "xls":
          case "xlsx":
            return [
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ];
          case "ppt":
          case "pptx":
            return [
              "application/vnd.ms-powerpoint",
              "application/vnd.openxmlformats-officedocument.presentationml.presentation"
            ];

          case "htm":
          case "html":
            return "text/html";
          case "css":
            return "text/css";
          case "js":
          case "javascript":
            return "application/javascript";
          case "ts":
            return "application/typecript";
          case "json":
            return "application/json";

          case "pdf":
            return "application/pdf";
        }
      });

      const file_types: Array<string> = _.flatten(formated_file_types);
      file_types.forEach(file_type => {
        if (value.indexOf(file_type.replace("/*", "/")) > -1) {
          valid_type = true;
        }
      });

      if (!valid_type) {
        return {
          fileType: {
            requiredFileTypes: file_types,
            actualFileType: current_type
          }
        };
      }
      return null;
    };
    return validator_fn;
  }
}

// Mensagens de erro de validaÃ§Ã£o
export const FormMessages: FormSchemaValidationMessages = {
  custom: (error, schema) => schema.custom_error_message,
  required: "Campo obrigatÃ³rio",
  pattern: "Formato invÃ¡lido",
  maxLength: error =>
    "NÃ£o pode ser maior que " + error.requiredLength + " caracteres",
  minLength: error =>
    "NÃ£o pode ser menor que " + error.requiredLength + " caracteres",
  email: "Formato de e-mail invÃ¡lido",
  phone: "Formato de telefone invÃ¡lido",
  cpf: "Formato de CPF invÃ¡lido",
  cnpj: "Formato de CNPJ invÃ¡lido",
  rg: "Formato de RG invÃ¡lido",
  cep: "Formato de CEP invÃ¡lido",
  date: "Formato de data invÃ¡lido",
  minDate: error =>
    `NÃ£o pode ser antes de ${moment(error.requiredMinDate).format(
      "DD/MM/YYYY"
    )}`,
  maxDate: error =>
    `NÃ£o pode ser depois de ${moment(error.requiredMaxDate).format(
      "DD/MM/YYYY"
    )}`,
  time: "Formato de hora invÃ¡lido",
  minTime: error => `NÃ£o pode ser menor de ${error.requiredMinTime}`,
  maxTime: error => `NÃ£o pode ser maior de ${error.requiredMaxTime}`,
  min: error => `NÃ£o pode ser menor que ${error.requiredMinValue}`,
  max: error => `NÃ£o pode ser maior que ${error.requiredMaxValue}`,
  equals: error => `Precisa ser igual a "${error.notEqualsSchema.label}"`,
  fileType: "Formato de arquivo invÃ¡lido"
};

/**
 * @ignore
 */
// Valida se o valor de um input Ã© vazio
function isEmptyInputValue(value: any): boolean {
  // we don't check for string here so it also works with arrays
  return value == null || value.length === 0;
}

/**
 * @ignore
 */
// Analisa propriedades do elemento e retorna os validadores
export function GetSchemaValidators(schema: FormSchema): Array<ValidatorFn> {
  const control_validators = [];
  const validators = [];

  if (!schema.validations) {
    schema.validations = {};
  }

  if (
    schema.type == "date" &&
    !schema.validations.date &&
    !Modernizr.inputtypes.date
  ) {
    schema.validations.date = true;
  }

  if (
    schema.type == "time" &&
    !schema.validations.time &&
    !Modernizr.inputtypes.time
  ) {
    schema.validations.date = true;
  }

  if (schema.type == "email") {
    schema.validations.email = true;
  }

  _.forEach(schema.validations, (v_value, v_key) => {
    if (_.has(FormValidators, v_key)) {
      const validator = FormValidators[v_key](v_value, schema.key, schema.form);
      if (validator) {
        control_validators.push(validator);
      }
    }
  });

  switch (schema.type) {
    case "email":
      validators.push(FormValidators.email(true, schema.key, schema.form));
      break;
  }

  if (_.isEmpty(validators)) {
    validators.push(Validators.nullValidator);
  }

  validators.push(FormValidators.custom(null, schema.key, schema.form));

  if (_.isEmpty(control_validators)) {
    return validators;
  }

  return validators.concat(control_validators);
}

export type FormValidationDateRule = _moment.Moment | Date | string;

export type FormValidationStringFunction = (
  schema: FormSchema,
  form: FormData
) => string;
export type FormValidationNumberFunction = (
  schema: FormSchema,
  form: FormData
) => number;
export type FormValidationBooleanFunction = (
  schema: FormSchema,
  form: FormData
) => boolean;
export type FormValidationRegExpFunction = (
  schema: FormSchema,
  form: FormData
) => string | RegExp;
export type FormValidationDateFunction = (
  schema: FormSchema,
  form: FormData
) => FormValidationDateRule;
export type FormValidationArrayStringFunction = (
  schema: FormSchema,
  form: FormData
) => string | Array<string>;

export type FunctionValidationMessage = (
  error: ValidationErrors,
  schema: FormSchema
) => string;
export type FunctionValidationCustomMessage = (
  error: string,
  schema: FormSchema
) => string;

export type FormSchemaValidationMessage =
  | string
  | FunctionValidationMessage
  | FunctionValidationCustomMessage;
